import axios from 'axios'
import type { AgentAvatarRef } from '@ponder/artifacts/agent-avatar'
import { auth } from './firebase'

export interface AdminAvatarRef {
  artifactId: string
  version: number
  svg?: string
}

export interface AdminAvatarOption {
  artifactId: string
  version: number
  name: string
  svg: string | null
  family?: string
  isDefault: boolean
}

export interface AdminAgentAvatarState {
  avatar: AdminAvatarRef | null
  source: 'agent' | 'legacy' | 'template' | 'org-default' | null
  own: boolean
}

// Get backend URL from environment or default to localhost
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3007'

// Org slug state for X-Org-Slug header injection.
// Persisted so a full page reload on org switch keeps the selected org (the
// header must be correct before any page data is fetched).
const ORG_SLUG_STORAGE_KEY = 'admin.currentOrgSlug'
let currentOrgSlug: string | null =
  typeof localStorage !== 'undefined' ? localStorage.getItem(ORG_SLUG_STORAGE_KEY) : null
export function setCurrentOrgSlug(slug: string | null) {
  currentOrgSlug = slug
  if (typeof localStorage !== 'undefined') {
    if (slug) localStorage.setItem(ORG_SLUG_STORAGE_KEY, slug)
    else localStorage.removeItem(ORG_SLUG_STORAGE_KEY)
  }
}

// Create axios instance
export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 10000,
  headers: {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  }
})

// Request interceptor to add auth token and org slug
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser
    if (user) {
      try {
        const token = await user.getIdToken()
        config.headers.Authorization = `Bearer ${token}`
      } catch (error) {
        console.error('Failed to get auth token:', error)
      }
    }
    if (currentOrgSlug) {
      config.headers['X-Org-Slug'] = currentOrgSlug
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - try to refresh
      const user = auth.currentUser
      if (user) {
        try {
          await user.getIdToken(true) // Force token refresh
          // Retry the original request
          const originalRequest = error.config
          if (!originalRequest._retry) {
            originalRequest._retry = true
            const newToken = await user.getIdToken()
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return api(originalRequest)
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError)
          // Redirect to login or logout user
          window.location.href = '/login'
        }
      }
    }
    return Promise.reject(error)
  }
)

// Admin validation API
export const adminApi = {
  // Check if current user has admin role
  checkAdminStatus: async (): Promise<{ isAdmin: boolean; role: string; permissions: string[] }> => {
    try {
      console.log('🌐 Making request to /api/users/me')
      const response = await api.get('/users/me')
      console.log('🔍 Raw response:', response.data)
      const user = response.data
      
      const result = {
        isAdmin: user.isPlatformAdmin === true || user.role === 'Admin' || user.role === 'Editor',
        role: user.role,
        permissions: user.permissions || []
      }
      console.log('🔍 Processed result:', result)
      
      return result
    } catch (error) {
      console.error('❌ Admin status check failed:', error)
      
      // If we get a 403/401, it might be a stale token issue
      if ((error as any).response?.status === 403 || (error as any).response?.status === 401) {
        console.log('Attempting token refresh due to permission error...')
        
        // Try to refresh the token and retry once
        const user = auth.currentUser
        if (user) {
          try {
            await user.getIdToken(true) // Force token refresh
            console.log('Token refreshed, retrying admin status check...')
            
            // Retry the request with fresh token
            const retryResponse = await api.get('/users/me')
            const retryUser = retryResponse.data
            
            return {
              isAdmin: retryUser.isPlatformAdmin === true || retryUser.role === 'Admin' || retryUser.role === 'Editor',
              role: retryUser.role,
              permissions: retryUser.permissions || []
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError)
          }
        }
      }
      
      throw error
    }
  },

  // Get current user profile
  getCurrentUser: async () => {
    const response = await api.get('/users/me')
    return response.data
  },

  // Test admin API access
  testAdminAccess: async () => {
    try {
      const response = await api.get('/users', { params: { limit: 1 } })
      return { success: true, data: response.data }
    } catch (error: any) {
      return { success: false, error: error.response?.data || error.message }
    }
  },

  // Force refresh Firebase token
  refreshToken: async () => {
    const user = auth.currentUser
    if (!user) {
      throw new Error('No authenticated user')
    }
    
    try {
      console.log('Forcing Firebase token refresh...')
      await user.getIdToken(true) // Force refresh
      console.log('Token refresh completed')
      return { success: true }
    } catch (error: any) {
      console.error('Token refresh failed:', error)
      return { success: false, error: error.message }
    }
  },

  // Agent management API methods
  getAgents: async (params?: { offset?: number; limit?: number; sortBy?: string; sortOrder?: string; visibility?: string }) => {
    const queryParams = new URLSearchParams()
    if (params?.offset) queryParams.append('offset', params.offset.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    if (params?.visibility) queryParams.append('visibility', params.visibility)
    // Admin panel should see all agent templates including hidden ones
    queryParams.append('includeHidden', 'true')
    queryParams.append('templatesOnly', 'true')

    const response = await api.get(`/agents?${queryParams.toString()}`)
    return { data: response.data, totalCount: parseInt(response.headers['x-total-count'] || '0', 10) }
  },

  getAgent: async (id: string) => {
    const response = await api.get(`/agents/${id}`)
    return response.data
  },

  getAgentDetails: async (id: string) => {
    const response = await api.get(`/agents/${id}/details`)
    return response.data
  },

  createAgent: async (agentData: {
    email: string
    name: string
    description?: string
    shortDescription?: string
    isDefault?: boolean
  }) => {
    const response = await api.post('/agents', agentData)
    return response.data
  },

  updateAgent: async (id: string, agentData: any) => {
    const response = await api.put(`/agents/${id}`, agentData)
    return response.data
  },

  deleteAgent: async (id: string) => {
    const response = await api.delete(`/agents/${id}`)
    return response.data
  },

  // Agent avatars (docs/avatar-integration-spec.md §2.4): a binding to an
  // Avatar artifact, never avatar_url. `target: 'template'` binds the
  // catalogue template (what every instance of it shows).
  getAvatarLibrary: async (): Promise<AdminAvatarOption[]> => {
    const response = await api.get('/agents/avatar/library')
    return response.data?.avatars ?? []
  },

  getAgentAvatar: async (id: string): Promise<AdminAgentAvatarState> => {
    const response = await api.get(`/agents/${id}/avatar`, { params: { target: 'template' } })
    return response.data
  },

  setAgentAvatar: async (id: string, artifactId: string): Promise<AdminAgentAvatarState> => {
    const response = await api.put(`/agents/${id}/avatar`, { artifactId, target: 'template' })
    return response.data
  },

  resetAgentAvatar: async (id: string): Promise<AdminAgentAvatarState> => {
    const response = await api.delete(`/agents/${id}/avatar`, { params: { target: 'template' } })
    return response.data
  },

  bulkCreateAgents: async (agentsData: Array<{
    email: string
    name: string
    description?: string
    shortDescription?: string
    isDefault?: boolean
  }>) => {
    const response = await api.post('/agents/batch', agentsData)
    return response.data
  },

  starAgent: async (id: string) => {
    const response = await api.post(`/agents/${id}/star`)
    return response.data
  },

  incrementAgentMessages: async (id: string) => {
    const response = await api.post(`/agents/${id}/increment-message-count`)
    return response.data
  },

  incrementAgentCorrespondence: async (id: string) => {
    const response = await api.post(`/agents/${id}/increment-correspondence`)
    return response.data
  },

  // Agent Knowledge Base associations
  getAgentKnowledgeBases: async (id: string) => {
    const response = await api.get(`/agents/${id}/knowledge-bases`)
    return response.data
  },

  addAgentKnowledgeBase: async (id: string, knowledgeBaseId: string) => {
    const response = await api.post(`/agents/${id}/knowledge-bases`, { knowledgeBaseId })
    return response.data
  },

  removeAgentKnowledgeBase: async (id: string, kbId: string) => {
    const response = await api.delete(`/agents/${id}/knowledge-bases/${kbId}`)
    return response.data
  },

  updateAgentKnowledgeBases: async (id: string, knowledgeBaseIds: string[]) => {
    const response = await api.put(`/agents/${id}/knowledge-bases`, { knowledgeBaseIds })
    return response.data
  },

  // Platform Settings API methods
  getPlatformSettings: async () => {
    const response = await api.get('/platform-settings')
    return response.data
  },

  getLLMModelSettings: async () => {
    const response = await api.get('/platform-settings/llm/models')
    return response.data
  },

  // Each active LLM model's resolved tool wire format, from the chain's own resolver.
  getToolWireFormats: async () => {
    const response = await api.get('/platform-settings/llm/tool-wire-formats')
    return response.data
  },

  updatePlatformSetting: async (key: string, value: string) => {
    const response = await api.put(`/platform-settings/${key}`, { value })
    return response.data
  },

  updateDefaultLLMModel: async (modelId: string) => {
    const response = await api.put('/platform-settings/llm/default-model', { modelId })
    return response.data
  },

  updateDefaultToolModel: async (modelId: string) => {
    const response = await api.put('/platform-settings/llm/default-tool-model', { modelId })
    return response.data
  },

  updateAvailableLLMModels: async (models: Array<{
    id: string
    name: string
    provider: string
    description: string
  }>) => {
    const response = await api.put('/platform-settings/llm/available-models', { models })
    return response.data
  },

  updateAvailableToolModels: async (models: Array<{
    id: string
    name: string
    provider: string
    description: string
  }>) => {
    const response = await api.put('/platform-settings/llm/available-tool-models', { models })
    return response.data
  },

  // Image Model Settings API methods
  getImageModelSettings: async () => {
    const response = await api.get('/platform-settings/image/models')
    return response.data
  },

  updateDefaultImageModel: async (modelId: string) => {
    const response = await api.put('/platform-settings/image/default-model', { modelId })
    return response.data
  },

  updateDefaultImageToImageModel: async (modelId: string) => {
    const response = await api.put('/platform-settings/image/default-image-to-image-model', { modelId })
    return response.data
  },

  // Image Styles API methods
  getImageStyles: async () => {
    const response = await api.get('/platform-settings/image/styles')
    return response.data
  },

  updateImageStyles: async (styles: Array<{
    id: string
    name: string
    description?: string
    stylePrompt: string
    negativePrompt?: string
    preferredModel?: string
    enabled: boolean
    order: number
  }>) => {
    const response = await api.put('/platform-settings/image/styles', { styles })
    return response.data
  },

  updateImageStyle: async (styleId: string, update: {
    name?: string
    description?: string
    stylePrompt?: string
    negativePrompt?: string
    preferredModel?: string
    enabled?: boolean
    order?: number
  }) => {
    const response = await api.put(`/platform-settings/image/styles/${styleId}`, update)
    return response.data
  },

  // User Management API methods
  getUsers: async (params?: {
    search?: string
    role?: string
    status?: string
    limit?: number
    offset?: number
    sortBy?: string
    sortOrder?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.search) queryParams.append('search', params.search)
    if (params?.role) queryParams.append('role', params.role)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder)

    const response = await api.get(`/users?${queryParams.toString()}`)
    return response.data
  },

  getUser: async (id: string) => {
    const response = await api.get(`/users/${id}`)
    return response.data
  },

  createUser: async (userData: {
    email: string
    firstName?: string
    lastName?: string
    role?: string
    plan?: string
    preferredModel?: string
    timezone?: string
    inviteQuota?: number
  }) => {
    const response = await api.post('/users', userData)
    return response.data
  },

  updateUser: async (id: string, userData: {
    email?: string
    firstName?: string
    lastName?: string
    role?: string
    status?: string
    plan?: string
    preferredModel?: string
    timezone?: string
    credits?: number
    inviteQuota?: number
    globallyUnsubscribed?: boolean
  }) => {
    console.log('Sending update user request with data:', userData)
    const response = await api.put(`/users/${id}`, userData)
    return response.data
  },

  getDeletionPreview: async (id: string) => {
    const response = await api.get(`/users/${id}/deletion-preview`)
    return response.data
  },

  deleteUser: async (id: string) => {
    const response = await api.delete(`/users/${id}`)
    return response.data
  },

  // User status management
  updateUserStatus: async (id: string, status: string, reason?: string) => {
    const response = await api.put(`/users/${id}/status`, { status, reason })
    return response.data
  },

  suspendUser: async (id: string, reason: string) => {
    const response = await api.post(`/users/${id}/suspend`, { reason })
    return response.data
  },

  activateUser: async (id: string) => {
    const response = await api.post(`/users/${id}/activate`)
    return response.data
  },

  // Credit management
  adjustUserCredits: async (id: string, amount: number, reason?: string) => {
    const response = await api.put(`/users/${id}/credits`, { amount, reason })
    return response.data
  },

  addUserCredits: async (id: string, amount: number, reason?: string) => {
    const response = await api.post(`/users/${id}/credits/add`, { amount, reason })
    return response.data
  },

  deductUserCredits: async (id: string, amount: number, reason?: string) => {
    const response = await api.post(`/users/${id}/credits/deduct`, { amount, reason })
    return response.data
  },

  // Bulk operations
  bulkUpdateUsers: async (userIds: string[], updates: any) => {
    const response = await api.post('/users/bulk-update', { userIds, updates })
    return response.data
  },

  bulkDeleteUsers: async (userIds: string[]) => {
    const response = await api.post('/users/bulk-delete', { userIds })
    return response.data
  },

  // User analytics
  getUserActivity: async (id: string) => {
    const response = await api.get(`/users/${id}/activity`)
    return response.data
  },

  getUserUsage: async (id: string) => {
    const response = await api.get(`/users/${id}/usage`)
    return response.data
  },

  getUserStats: async () => {
    const response = await api.get('/users/stats')
    return response.data
  },

  // Knowledge Base Management API methods
  getKnowledgeBases: async (params?: {
    search?: string
    status?: string
    limit?: number
    offset?: number
    sortBy?: string
    sortOrder?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.search) queryParams.append('search', params.search)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder)

    const response = await api.get(`/knowledge-bases?${queryParams.toString()}`)
    return response.data
  },

  getKnowledgeBase: async (id: string) => {
    const response = await api.get(`/knowledge-bases/${id}`)
    return response.data
  },

  createKnowledgeBase: async (kbData: {
    name: string
    description?: string
    schema?: any
    metadata?: any
  }) => {
    const response = await api.post('/knowledge-bases', kbData)
    return response.data
  },

  updateKnowledgeBase: async (id: string, kbData: {
    name?: string
    description?: string
    schema?: any
    metadata?: any
    status?: string
  }) => {
    const response = await api.put(`/knowledge-bases/${id}`, kbData)
    return response.data
  },

  deleteKnowledgeBase: async (id: string) => {
    const response = await api.delete(`/knowledge-bases/${id}`)
    return response.data
  },

  // Knowledge Base document operations
  getKnowledgeBaseDocuments: async (kbId: string, params?: {
    search?: string
    limit?: number
    offset?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())

    const response = await api.get(`/knowledge-bases/${kbId}/documents?${queryParams.toString()}`)
    return response.data
  },

  createKnowledgeBaseDocument: async (kbId: string, docData: any) => {
    const response = await api.post(`/knowledge-bases/${kbId}/documents`, docData)
    return response.data
  },

  updateKnowledgeBaseDocument: async (kbId: string, docId: string, docData: any) => {
    const response = await api.patch(`/knowledge-bases/${kbId}/documents/${docId}`, docData)
    return response.data
  },

  deleteKnowledgeBaseDocument: async (kbId: string, docId: string) => {
    const response = await api.delete(`/knowledge-bases/${kbId}/documents/${docId}`)
    return response.data
  },

  searchKnowledgeBase: async (kbId: string, params: {
    query: string
    limit?: number
  }) => {
    const response = await api.post(`/knowledge-bases/${kbId}/search`, params)
    return response.data
  },

  textSearchKnowledgeBase: async (kbId: string, params: {
    query: string
    limit?: number
    filters?: any[]
  }) => {
    const response = await api.post(`/knowledge-bases/${kbId}/search/text`, params)
    return response.data
  },

  hybridSearchKnowledgeBase: async (kbId: string, params: {
    query: string
    limit?: number
    filters?: any[]
  }) => {
    const response = await api.post(`/knowledge-bases/${kbId}/search/hybrid`, params)
    return response.data
  },

  // Knowledge Base bulk operations
  importKnowledgeBaseData: async (kbId: string, formData: FormData) => {
    const response = await api.post(`/knowledge-bases/${kbId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  importKnowledgeBaseFile: async (kbId: string, formData: FormData) => {
    const response = await api.post(`/knowledge-bases/${kbId}/import/file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  importKnowledgeBaseUrl: async (kbId: string, data: {
    url: string
    format: 'json' | 'csv'
    mapping?: Record<string, string>
    imageField?: string
  }) => {
    const response = await api.post(`/knowledge-bases/${kbId}/import/url`, data)
    return response.data
  },

  validateImport: async (kbId: string, data: {
    source: 'file' | 'url'
    url?: string
    format: 'json' | 'csv'
    mapping?: Record<string, string>
    imageField?: string
  }) => {
    const response = await api.post(`/knowledge-bases/${kbId}/import/validate`, data)
    return response.data
  },

  exportKnowledgeBase: async (kbId: string, format: 'json' | 'csv' = 'json') => {
    const response = await api.get(`/knowledge-bases/${kbId}/export?format=${format}`, {
      responseType: 'blob'
    })
    return response.data
  },


  getKnowledgeBaseStats: async () => {
    const response = await api.get('/knowledge-bases/stats')
    return response.data
  },

  validateKnowledgeBaseSchema: async (kbId: string, schema: any) => {
    const response = await api.post(`/knowledge-bases/${kbId}/validate-schema`, { schema })
    return response.data
  },

  // Vectorization operations
  vectorizeDocuments: async (kbId: string, data: {
    all: boolean
    documentIds?: string[]
  }) => {
    const response = await api.post(`/knowledge-bases/${kbId}/vectorize`, data)
    return response.data
  },

  getVectorizationStatus: async (kbId: string) => {
    const response = await api.get(`/knowledge-bases/${kbId}/vectorize/status`)
    return response.data.data
  },

  // Credit Tracking API methods
  getUsersWithCredits: async (params?: {
    limit?: number
    offset?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())

    const response = await api.get(`/admin/credit-tracking/users-with-credits?${queryParams.toString()}`)
    return response.data
  },

  getUserCreditDetails: async (userId: string) => {
    const response = await api.get(`/admin/credit-tracking/user-details/${userId}`)
    return response.data
  },

  getUserBillingHistory: async (userId: string, params?: {
    limit?: number
    offset?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())

    const response = await api.get(`/admin/credit-tracking/user/${userId}/billing-history?${queryParams.toString()}`)
    return response.data
  },

  getUserRecentActivities: async (userId: string, params?: {
    limit?: number
    offset?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())

    const response = await api.get(`/admin/credit-tracking/user/${userId}/recent-activities?${queryParams.toString()}`)
    return response.data
  },

  assignCredits: async (userId: string, data: {
    credits: number
    reason?: string
    isBonus?: boolean
  }) => {
    const response = await api.post(`/admin/credit-tracking/user/${userId}/assign-credits`, data)
    return response.data
  },

  deductCredits: async (userId: string, data: {
    credits: number
    reason?: string
  }) => {
    const response = await api.post(`/admin/credit-tracking/user/${userId}/deduct-credits`, data)
    return response.data
  },

  // --- Per-user monthly spend caps (org-scoped, /api/org/costs) ---
  //
  // Distinct from credits and from tiers. A cap is a hard refusal in dollars
  // on the calendar month, checked before a request reaches the provider; a
  // tier is a monthly credit allowance. The endpoints report the same figure
  // in dollars, in credits and in pre-margin provider cost, so a screen never
  // has to do that conversion itself and drift from the server.
  getSpendLimit: async (userId: string) => {
    const response = await api.get(`/org/costs/limits/${userId}`)
    return response.data.data ?? response.data
  },

  setSpendLimit: async (userId: string, data: {
    monthlyCapUsd: number | null
    enabled?: boolean
    warnAtPercent?: number
    notes?: string
  }) => {
    const response = await api.put(`/org/costs/limits/${userId}`, data)
    return response.data.data ?? response.data
  },

  removeSpendLimit: async (userId: string) => {
    const response = await api.delete(`/org/costs/limits/${userId}`)
    return response.data
  },

  /** The org's billing multiplier and credit rate. 1.0 means billed at cost. */
  getOrgMargin: async () => {
    const response = await api.get('/org/costs/margin')
    return response.data.data ?? response.data
  },

  // Subscription Tier Management
  getSubscriptionTiers: async () => {
    const response = await api.get('/credit-tracking/tiers')
    // API returns { success: true, data: [...] }
    return response.data.data || response.data.tiers || response.data
  },

  adjustSubscription: async (userId: string, tierId: string) => {
    const response = await api.post('/admin/credit-tracking/adjust-subscription', { userId, tierId })
    return response.data
  },

  removeTierOverride: async (userId: string) => {
    const response = await api.post('/admin/credit-tracking/remove-tier-override', { userId })
    return response.data
  },

  // Get user's current subscription details
  getUserSubscription: async (userId: string) => {
    const response = await api.get(`/admin/credit-tracking/user-details/${userId}`)
    return response.data?.data?.subscription || null
  },

  // Consent Policy Management API methods
  getConsentPolicies: async () => {
    const response = await api.get('/consent-policies')
    return response.data
  },

  createConsentPolicy: async (policyData: {
    name: string
    version: string
    policyText: string
    effectiveDate?: string
  }) => {
    const response = await api.post('/consent-policies', policyData)
    return response.data
  },

  getActiveConsentPolicy: async (name: string) => {
    const response = await api.get(`/consent-policies/${name}`)
    return response.data
  },

  activateConsentPolicy: async (policyId: string) => {
    const response = await api.post(`/consent-policies/${policyId}/activate`)
    return response.data
  },

  // Workflow Management API methods
  getWorkflows: async (params?: {
    limit?: number
    offset?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())

    const response = await api.get(`/workflows?${queryParams.toString()}`)
    return response.data
  },

  getLocalWorkflowsMetadata: async () => {
    const response = await api.get('/workflows/local/metadata')
    return response.data
  },

  getLocalWorkflowContent: async (filename: string) => {
    const response = await api.get(`/workflows/local/content/${filename}`)
    return response.data
  },

  getLocalWorkflowJson: async (filename: string) => {
    const response = await api.get(`/workflows/local/json/${filename}`)
    return response.data
  },

  // API Token management methods
  createApiToken: async (userId: string, name: string, expiresAt?: string | null) => {
    const response = await api.post('/admin/api-tokens', {
      userId,
      name,
      expiresAt
    })
    return response.data
  },

  listApiTokens: async (userId: string, includeRevoked: boolean = false) => {
    const response = await api.get(`/admin/api-tokens/user/${userId}`, {
      params: { includeRevoked }
    })
    return response.data
  },

  revokeApiToken: async (tokenId: string) => {
    const response = await api.delete(`/admin/api-tokens/${tokenId}`)
    return response.data
  },

  // Admin Tools - LLM Inference
  llmInfer: async (params: {
    provider: 'openrouter' | 'azure' | 'google'
    model: string
    systemPrompt: string
    userPrompt: string
    temperature?: number
    maxTokens?: number
  }) => {
    const response = await api.post('/admin/llm/infer', params, {
      timeout: 120000, // 120 second timeout for LLM inference
    })
    return response.data
  },

  getLLMProviders: async () => {
    const response = await api.get('/admin/llm/providers')
    return response.data
  },

  // Admin Tools - Image Management
  uploadAsset: async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post('/admin/assets/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 second timeout for file uploads
    })
    return response.data
  },

  generateImage: async (params: {
    prompt: string
    model: string
    width?: number
    height?: number
    steps?: number
    seed?: number
    negativePrompt?: string
    runwareModelId?: string
    inputImage?: string
  }) => {
    const response = await api.post('/admin/images/generate', params, {
      timeout: 120000, // 2 minute timeout for image generation
    })
    return response.data
  },

  getImageModels: async () => {
    const response = await api.get('/admin/images/models')
    return response.data
  },

  deleteImage: async (url: string) => {
    const response = await api.delete('/admin/images', {
      data: { url },
    })
    return response.data
  },

  // Dashboard Stats
  getDashboardStats: async () => {
    const response = await api.get('/admin/dashboard/stats')
    return response.data
  },

  // Policy Administration API methods
  getPolicyOverview: async () => {
    const response = await api.get('/admin/policies/overview')
    return response.data
  },

  getDefaultPolicies: async () => {
    const response = await api.get('/admin/policies/defaults')
    return response.data
  },

  getPolicyStats: async () => {
    const response = await api.get('/admin/policies/stats')
    return response.data
  },

  getPolicyMetrics: async () => {
    const response = await api.get('/admin/policies/metrics')
    return response.data
  },

  resetPolicyMetrics: async () => {
    const response = await api.post('/admin/policies/metrics/reset')
    return response.data
  },

  listPolicies: async (params?: {
    page?: number
    limit?: number
    resourceType?: string
    subjectType?: string
    effect?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.resourceType) queryParams.append('resourceType', params.resourceType)
    if (params?.subjectType) queryParams.append('subjectType', params.subjectType)
    if (params?.effect) queryParams.append('effect', params.effect)

    const response = await api.get(`/admin/policies/list?${queryParams.toString()}`)
    return response.data
  },

  listPolicyRequests: async (params?: {
    page?: number
    limit?: number
    requestType?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.requestType) queryParams.append('requestType', params.requestType)

    const response = await api.get(`/admin/policies/requests?${queryParams.toString()}`)
    return response.data
  },

  // Chain Log Admin API methods
  getUserChainLogStatus: async (userId: string) => {
    const response = await api.get(`/admin/chain-logs/users/${userId}/status`)
    return response.data
  },

  toggleUserChainLogging: async (userId: string, enabled: boolean) => {
    const response = await api.put(`/admin/chain-logs/users/${userId}/toggle`, { enabled })
    return response.data
  },

  getUserChainLogConversations: async (userId: string) => {
    const response = await api.get(`/admin/chain-logs/users/${userId}`)
    return response.data
  },

  getUserChainLogs: async (userId: string, conversationId: string) => {
    const response = await api.get(`/admin/chain-logs/users/${userId}/conversations/${conversationId}`)
    return response.data
  },

  getChainLog: async (userId: string, conversationId: string, filename: string) => {
    const response = await api.get(`/admin/chain-logs/users/${userId}/conversations/${conversationId}/${filename}`)
    return response.data
  },

  // Pricing Settings API methods
  getPricingSettings: async () => {
    const response = await api.get('/platform-settings/pricing/settings')
    return response.data
  },

  updatePricingSettings: async (settings: {
    marginPercent?: number
    creditsPerDollar?: number
    appStoreFeePercent?: number
  }) => {
    const response = await api.put('/platform-settings/pricing/settings', settings)
    return response.data
  },

  getTierCostAnalysis: async (avgUtilization: number = 0.6) => {
    const response = await api.get(`/platform-settings/pricing/tiers?avgUtilization=${avgUtilization}`)
    return response.data
  },

  updateTierPricing: async (tierId: string, dto: {
    monthlyCredits?: number
    monthlyPrice?: number
    yearlyPrice?: number
  }) => {
    const response = await api.put(`/platform-settings/pricing/tiers/${tierId}`, dto)
    return response.data
  },

  getAuditLogs: async (params?: {
    page?: number
    limit?: number
    action?: string
    category?: string
    actorType?: string
    outcome?: string
    actorId?: string
    resourceType?: string
    startDate?: string
    endDate?: string
    search?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.action) queryParams.append('action', params.action)
    if (params?.category) queryParams.append('category', params.category)
    if (params?.actorType) queryParams.append('actorType', params.actorType)
    if (params?.outcome) queryParams.append('outcome', params.outcome)
    if (params?.actorId) queryParams.append('actorId', params.actorId)
    if (params?.resourceType) queryParams.append('resourceType', params.resourceType)
    if (params?.startDate) queryParams.append('startDate', params.startDate)
    if (params?.endDate) queryParams.append('endDate', params.endDate)
    if (params?.search) queryParams.append('search', params.search)

    const response = await api.get(`/admin/audit-logs?${queryParams.toString()}`)
    return response.data
  },

  // --- Organization Management (Platform Admin) ---

  getOrganizations: async () => {
    const response = await api.get('/platform/orgs')
    return response.data
  },

  getOrganization: async (slug: string) => {
    const response = await api.get(`/platform/orgs/${slug}`)
    return response.data
  },

  createOrganization: async (data: { slug: string; name: string; plan?: string; initialAdminEmails?: string[] }) => {
    const response = await api.post('/platform/orgs', data)
    return response.data
  },

  updateOrganization: async (slug: string, data: { name?: string; plan?: string; customDomain?: string; status?: string }) => {
    const response = await api.patch(`/platform/orgs/${slug}`, data)
    return response.data
  },

  // --- Org Members (requires X-Org-Slug header) ---

  getOrgMembers: async (params?: { role?: string; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams()
    if (params?.role) queryParams.append('role', params.role)
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    const response = await api.get(`/org/members?${queryParams.toString()}`)
    return response.data
  },

  updateMemberRole: async (userId: string, role: string) => {
    const response = await api.patch(`/org/members/${userId}/role`, { role })
    return response.data
  },

  removeMember: async (userId: string) => {
    const response = await api.delete(`/org/members/${userId}`)
    return response.data
  },

  // --- Org Invites (requires X-Org-Slug header) ---

  createInvite: async (email: string, role: string) => {
    const response = await api.post('/org/invites', { email, role })
    return response.data
  },

  getInvites: async (status?: string) => {
    const queryParams = new URLSearchParams()
    if (status) queryParams.append('status', status)
    const response = await api.get(`/org/invites?${queryParams.toString()}`)
    return response.data
  },

  revokeInvite: async (inviteId: string) => {
    const response = await api.delete(`/org/invites/${inviteId}`)
    return response.data
  },

  resendInvite: async (inviteId: string) => {
    const response = await api.post(`/org/invites/${inviteId}/resend`)
    return response.data
  },

  acceptInviteAdmin: async (inviteId: string) => {
    const response = await api.post(`/org/invites/${inviteId}/accept`)
    return response.data
  },

  // --- Org Settings (requires X-Org-Slug header) ---

  getOrgSettings: async (category?: string) => {
    const queryParams = new URLSearchParams()
    if (category) queryParams.append('category', category)
    const response = await api.get(`/org/settings?${queryParams.toString()}`)
    return response.data
  },

  updateOrgSetting: async (key: string, value: any, category?: string, description?: string) => {
    const response = await api.put(`/org/settings/${key}`, { value, category, description })
    return response.data
  },
}

/**
 * Agent avatars (docs/avatar-integration-spec.md §2.2). Agent responses carry
 * `avatar` (the resolved Avatar artifact ref, with a static `svg`); these two
 * reads back the admin's AgentAvatar host when a
 * row has no inline `svg`.
 */
export type { AgentAvatarRef }

export interface AdminAvatarArtifact {
  type?: string
  version: number
  data?: unknown
}

export const avatarApi = {
  getAvatarArtifact: async (artifactId: string): Promise<AdminAvatarArtifact | null> => {
    const response = await api.get(`/artifacts/${encodeURIComponent(artifactId)}`)
    return response.data ?? null
  },

  getAgentAvatar: async (agentId: string): Promise<AgentAvatarRef | null> => {
    const response = await api.get(`/agents/${encodeURIComponent(agentId)}`)
    return (response.data?.avatar as AgentAvatarRef | undefined) ?? null
  },
}

export default api